import React, { useEffect, useRef, useState } from "react";
import { parse, isValid, differenceInYears } from "date-fns";
import axios from "axios";
import "./videorecorder.css";
import "./smartVitalsFlow.css";
import { notification, Modal } from "antd";
import { FaceDetector, FilesetResolver } from "@mediapipe/tasks-vision";
import { Check, AlertCircle } from "lucide-react";
import SmartVitalUpload from "../Quantum/SmartVitalUpload";
import { scanIcons } from "./svAssets";
import http from '../../api/Services/HttpCommon';
import { usePatientData } from "../../hooks/usePatientData";

const RECORD_SECONDS = 30;
const RING_R = 204;
const RING_C = 2 * Math.PI * RING_R;

// `bare` icons ship without the tile background, so we draw one around them.
const VITALS = [
  { label: "Heart rate", icon: scanIcons.heartRate },
  { label: "Oxygen level (SpO2)", icon: scanIcons.oxygen },
  { label: "Breathing rate", icon: scanIcons.breathe, bare: true },
  { label: "Heart rate variability", icon: scanIcons.hrv },
  { label: "Blood pressure", icon: scanIcons.bloodPressure },
  { label: "Haemoglobin", icon: scanIcons.haemoglobin },
  { label: "Stress index", icon: scanIcons.stressIndex },
];

const TIPS = [
  { label: "Good lighting", icon: scanIcons.goodLighting },
  { label: "Face centered", icon: scanIcons.faceCentered },
  { label: "Neutral expression", icon: scanIcons.neutralExp },
  { label: "No movement", icon: scanIcons.noMovement },
];

const PENDING = { ok: null, text: "Checking" };

// Live capture checks, computed from the camera preview + MediaPipe face box.
const computeChecks = (video, detections, sampleCanvas, lastCenterRef) => {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) return null;

  sampleCanvas.width = 32;
  sampleCanvas.height = 24;
  const ctx = sampleCanvas.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(video, 0, 0, 32, 24);
  const px = ctx.getImageData(0, 0, 32, 24).data;
  let sum = 0;
  for (let i = 0; i < px.length; i += 4) sum += 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
  const lum = sum / (px.length / 4);
  const lighting =
    lum < 70 ? { ok: false, text: "Too dark" } : lum > 215 ? { ok: false, text: "Too bright" } : { ok: true, text: "Good" };

  const faces = detections?.detections || [];
  const single =
    faces.length === 1 ? { ok: true, text: "Good" } : { ok: false, text: faces.length ? "Multiple" : "No face" };

  const box = faces.length === 1 ? faces[0].boundingBox : null;
  if (!box) {
    lastCenterRef.current = null;
    const missing = { ok: false, text: "—" };
    return { lighting, single, centred: missing, distance: missing, movement: missing };
  }

  const cx = box.originX + box.width / 2;
  const cy = box.originY + box.height / 2;
  const centred =
    Math.abs(cx - vw / 2) / vw < 0.15 && Math.abs(cy - vh / 2) / vh < 0.18
      ? { ok: true, text: "Good" }
      : { ok: false, text: "Adjust" };

  const ratio = box.width / vw;
  const distance =
    ratio < 0.2 ? { ok: false, text: "Move closer" } : ratio > 0.6 ? { ok: false, text: "Move back" } : { ok: true, text: "Good" };

  const last = lastCenterRef.current;
  const moved = last ? Math.hypot(cx - last.x, cy - last.y) / vw : 0;
  lastCenterRef.current = { x: cx, y: cy };
  const movement = moved < 0.04 ? { ok: true, text: "Good" } : { ok: false, text: "Hold still" };

  return { lighting, single, centred, distance, movement };
};

const CheckRow = ({ label, check }) => (
  <li className="sv-check">
    <span className={`sv-check-dot${check.ok === false ? " is-warn" : check.ok ? " is-ok" : ""}`}>
      {check.ok === false ? <AlertCircle size={11} /> : <Check size={11} strokeWidth={3} />}
    </span>
    <span className="sv-check-label">{label}</span>
    <span className={`sv-check-state${check.ok === false ? " is-warn" : check.ok ? " is-ok" : ""}`}>{check.text}</span>
  </li>
);

// Decorative trace; the actual signal is extracted server-side from the video.
const PulseTrace = ({ animate }) => (
  <svg className={`sv-pulse-trace${animate ? " is-live" : ""}`} viewBox="0 0 300 50" preserveAspectRatio="none" aria-hidden="true">
    <path
      d="M0 30 H40 L48 30 L54 8 L60 44 L66 30 H110 L118 30 L124 8 L130 44 L136 30 H180 L188 30 L194 8 L200 44 L206 30 H250 L258 30 L264 8 L270 44 L276 30 H300
         M300 30 H340 L348 30 L354 8 L360 44 L366 30 H410 L418 30 L424 8 L430 44 L436 30 H480 L488 30 L494 8 L500 44 L506 30 H550 L558 30 L564 8 L570 44 L576 30 H600"
      fill="none"
      stroke="#00BD7F"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
  </svg>
);

const VideoCaptureComponent = ({ formData, onCancel, onUploadPageChange }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [isCountdownActive, setIsCountdownActive] = useState(false);
  const [recordingTimeLeft, setRecordingTimeLeft] = useState(RECORD_SECONDS);
  const recordingTimerRef = useRef(null);
  // Refs, not state: these are read from interval/recorder callbacks that would
  // otherwise see stale values (the recorder could never be stopped on face loss).
  const recorderRef = useRef(null);
  const detectionIntervalRef = useRef(null);
  const faceDetectorRef = useRef(null);
  const sampleCanvasRef = useRef(null);
  const lastCenterRef = useRef(null);
  const [faceDetector, setFaceDetector] = useState(null);
  const [checks, setChecks] = useState({
    lighting: PENDING, single: PENDING, centred: PENDING, distance: PENDING, movement: PENDING,
  });
  const [isSubmitted] = useState(false);
  const [isPreparingDataLoading, setIsPreparingDataLoading] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  // New state for Quantum Upload
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [uploadStage, setUploadStage] = useState(null);
  const [userData] = useState(() => JSON.parse(localStorage.getItem("loginObj") || "{}"));
  const [patientDob, setPatientDob] = useState(userData?.dob || "");
  const [patientGender, setPatientGender] = useState(userData?.gender || "");
  const patientCode = userData?.code;
  const patientFirstName = userData?.firstName;
  const patientLastName = userData?.lastName;
  const patientMobile = userData?.mobile || "";
  const { patientData } = usePatientData();
  const loginObj = JSON.parse(localStorage.getItem("loginObj") || "{}");

  useEffect(() => {
    initializeMediaPipe();
    initializeCamera();

    // Fetch profile for dob/gender
    if (userData?.code) {
      http.get(`patient/${userData.code}`)
        .then(res => {
          const data = res?.data?.data || res?.data;
          if (data?.dob) setPatientDob(data.dob);
          if (data?.gender) setPatientGender(data.gender);
        })
        .catch(err => console.error("Profile fetch error:", err));
    }

    return () => { stopCamera(); cleanup(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializeMediaPipe = async () => {
    try {
      const vision = await FilesetResolver.forVisionTasks(
        "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.0/wasm"
      );

      const detector = await FaceDetector.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: "https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite",
          delegate: "GPU"
        },
        runningMode: "VIDEO",
        minDetectionConfidence: 0.5,
        minSuppressionThreshold: 0.3
      });

      faceDetectorRef.current = detector;
      setFaceDetector(detector);
      console.log("MediaPipe Face Detector initialized successfully");
    } catch (error) {
      console.error("Error initializing MediaPipe:", error);
      notification.error({
        message: "Face Detection Error",
        description: "Failed to initialize face detection. Recording will continue without face monitoring.",
        duration: 5,
        placement: "top",
      });
    }
  };

  const initializeCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // Handle play promise to avoid AbortError
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            if (error.name !== "AbortError") {
              console.error("Error playing video:", error);
            }
          });
        }
        console.log("Camera initialized successfully.");
        setIsCameraOn(true);
      }
    } catch (error) {
      console.error("Error accessing camera:", error);
      setShowCameraModal(true);
    }
  };

  const stopCamera = () => {
    if (videoRef.current) {
        if (videoRef.current.srcObject) {
            const stream = videoRef.current.srcObject;
            const tracks = stream.getTracks();
            tracks.forEach((track) => track.stop());
            videoRef.current.srcObject = null;
        }
        setIsCameraOn(false);
        console.log("Camera stopped successfully.");
    }
  };

  const cleanup = () => {
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
      detectionIntervalRef.current = null;
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    if (faceDetectorRef.current) {
      faceDetectorRef.current.close();
      faceDetectorRef.current = null;
    }
  };

  const detectFaces = async () => {
    const detector = faceDetectorRef.current;
    if (!detector || !videoRef.current || videoRef.current.readyState !== 4) {
      return null;
    }

    try {
      const startTimeMs = performance.now();
      const detections = detector.detectForVideo(videoRef.current, startTimeMs);
      return detections;
    } catch (error) {
      console.error("Face detection error:", error);
      return null;
    }
  };

  const updateChecks = (detections) => {
    if (!videoRef.current) return;
    if (!sampleCanvasRef.current) sampleCanvasRef.current = document.createElement("canvas");
    try {
      const next = computeChecks(videoRef.current, detections, sampleCanvasRef.current, lastCenterRef);
      if (next) setChecks(next);
    } catch (error) {
      console.error("Live check error:", error);
    }
  };

  // Preview checks while waiting to record; the recording loop takes over after that.
  useEffect(() => {
    if (!faceDetector || !isCameraOn || isRecording || showUploadModal) return undefined;
    const id = setInterval(async () => {
      const detections = await detectFaces();
      if (detections) updateChecks(detections);
    }, 700);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faceDetector, isCameraOn, isRecording, showUploadModal]);

  const startFaceDetection = () => {
    if (!faceDetectorRef.current) {
      console.warn("Face detector not initialized");
      return;
    }

    const interval = setInterval(async () => {
      const detections = await detectFaces();

      if (detections) {
        updateChecks(detections);

        const faceCount = detections.detections ? detections.detections.length : 0;

        if (faceCount === 0) {
          console.warn("No face detected. Stopping recording.");
          stopRecordingDueToFaceIssue("Face Not Detected",
            "Please adjust your position and try again. Ensure you are in a well-lit area and keep your face within the frame.");
        } else if (faceCount > 1) {
          console.warn("Multiple faces detected. Stopping recording.");
          stopRecordingDueToFaceIssue("Multiple Faces Detected",
            "Only one face is allowed. Please ensure only one person is in the frame and try recording again.");
        }
      }
    }, 1000);

    detectionIntervalRef.current = interval;
  };

  const stopFaceDetection = () => {
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
      detectionIntervalRef.current = null;
    }

    // Clear canvas
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
  };

  const resetRecordingState = () => {
    // Clear any existing timer first
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }

    setRecordingTimeLeft(RECORD_SECONDS);
    setCountdown(3);
    setIsRecording(false);
    setIsCountdownActive(false);
  };

  const stopRecordingDueToFaceIssue = (title, description) => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    }

    resetRecordingState();
    stopFaceDetection();
    // Camera stays on here — stopping it would trigger the camera-permission modal.

    notification.warning({
      message: title,
      description: description,
      duration: 5,
      placement: "top",
    });
  };

  const startRecording = async () => {
    try {
      if (!isCameraOn) {
        await initializeCamera();
      }

      if (!videoRef.current || !videoRef.current.srcObject) {
        console.warn("Camera is not initialized or video element is missing.");
        setShowCameraModal(true);
        return;
      }

      const streamCheck = videoRef.current.srcObject;
      const hasActiveVideo = streamCheck.getVideoTracks && streamCheck.getVideoTracks().some((t) => t.readyState === "live");
      if (!hasActiveVideo) {
        setShowCameraModal(true);
        return;
      }

      setIsCountdownActive(true);
      let timerValue = 3;
      setCountdown(timerValue);

      const countdownInterval = setInterval(() => {
        timerValue -= 1;
        setCountdown(timerValue);
        if (timerValue <= 0) {
          clearInterval(countdownInterval);
          setIsCountdownActive(false);
          setCountdown(3);
          beginRecording();
        }
      }, 1000);
    } catch (error) {
      console.error("Error in startRecording:", error);
      resetRecordingState();
      stopCamera();
      notification.error({
        message: "Recording Error",
        description: error.message,
      });
    }
  };

  const beginRecording = () => {
    const candidates = [
      "video/mp4;codecs=avc1",
      "video/webm;codecs=vp9",
      "video/webm;codecs=vp8",
      "video/webm",
      "video/mp4",
    ].filter((t) => {
      try {
        return MediaRecorder.isTypeSupported(t);
      } catch {
        return false;
      }
    });
    const mimeType = candidates.length > 0 ? candidates[0] : undefined;
    const stream = videoRef.current ? videoRef.current.srcObject : null;
    if (!stream) {
      setShowCameraModal(true);
      resetRecordingState();
      stopCamera();
      return;
    }
    const hasTracks = stream.getTracks && stream.getTracks().length > 0;
    const hasVideo = stream.getVideoTracks && stream.getVideoTracks().length > 0;
    if (!hasTracks || !hasVideo) {
      setShowCameraModal(true);
      resetRecordingState();
      stopCamera();
      return;
    }
    let recorder;
    try {
      recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    } catch (e) {
      try {
        recorder = new MediaRecorder(stream);
      } catch {
        notification.error({
          message: "Recording Error",
          description: "Recording format not supported on this browser.",
          duration: 5,
          placement: "top",
        });
        resetRecordingState();
        stopCamera();
        return;
      }
    }
    const chunks = [];
    let stoppedByTimer = false;

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunks.push(event.data);
      }
    };

    try {
      recorder.start();
    } catch (e) {
      notification.error({
        message: "Recording Error",
        description: "Unable to start recording. Check camera settings.",
        duration: 5,
        placement: "top",
      });
      resetRecordingState();
      stopCamera();
      return;
    }
    recorderRef.current = recorder;
    lastCenterRef.current = null;
    setIsRecording(true);

    startFaceDetection();

    let timeLeft = RECORD_SECONDS;
    setRecordingTimeLeft(timeLeft);

    // Clear any existing timer before starting new one
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
    }

    recordingTimerRef.current = setInterval(() => {
      timeLeft -= 1;
      setRecordingTimeLeft(timeLeft);
      if (timeLeft <= 0) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
        if (recorder.state !== "inactive") {
          stoppedByTimer = true;
          recorder.stop();
        }
        setIsRecording(false);
        stopFaceDetection();
      }
    }, 1000);

    recorder.onstop = async () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      stopFaceDetection();
      recorderRef.current = null;
      const blob = new Blob(chunks, { type: mimeType });
      if (stoppedByTimer === true) {
        uploadVideo(blob);
        stopCamera();
      }
    };
  };

  const stopRecording = () => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
      resetRecordingState();
      stopFaceDetection();
    }
  };

const calculateAge = (dob) => {
  if (!dob) return "";

  let birthDate;

  try {
    if (typeof dob === "string") {
      const trimmedDob = dob.trim();
      const formats = [
        "MM/dd/yyyy", // 08/15/1989
        "dd/MM/yyyy", // 15/08/1989
        "yyyy-MM-dd", // 1989-08-15 (API format)
      ];

      for (let format of formats) {
        const parsed = parse(trimmedDob, format, new Date());
        if (isValid(parsed)) {
          birthDate = parsed;
          break;
        }
      }
    } else {
      birthDate = new Date(dob);
    }

    if (!birthDate || !isValid(birthDate)) {
      console.warn("Invalid DOB:", dob);
      return "";
    }

    return differenceInYears(new Date(), birthDate);
  } catch (error) {
    console.error("Age calculation error:", error);
    return "";
  }
};

  const handleUploadComplete = async (requestId) => {
    const raw = sessionStorage.getItem("calculatedAnswers");
    const storedAnswers = raw ? JSON.parse(raw) : [];

    const q1 = Array.isArray(storedAnswers) && storedAnswers.length > 0 ? storedAnswers[0] : "";
    const q2 = Array.isArray(storedAnswers) && storedAnswers.length > 1 ? storedAnswers[1] : "";
    const q3 = Array.isArray(storedAnswers) && storedAnswers.length > 2 ? storedAnswers[2] : "";
    const q4 = Array.isArray(storedAnswers) && storedAnswers.length > 3 ? storedAnswers[3] : "";
    const q5 = Array.isArray(storedAnswers) && storedAnswers.length > 4 ? storedAnswers[4] : "";
    const q6 = Array.isArray(storedAnswers) && storedAnswers.length > 5 ? storedAnswers[5] : "";
    const q7 = Array.isArray(storedAnswers) && storedAnswers.length > 6 ? storedAnswers[6] : "";
    const q8 = Array.isArray(storedAnswers) && storedAnswers.length > 7 ? storedAnswers[7] : "";

   const age = String(calculateAge(formData?.dob || patientDob || userData?.dob || ""));
const gender = formData?.gender ||patientGender || userData?.gender ||  "";
const name = `${patientFirstName || ""} ${patientLastName || ""}`.trim();

    const mandatoryFields = {
      "Request ID": requestId,
      "Patient ID": patientCode,
      "Name": name,
      "Age": age,
      "Gender": gender,
      "Height": String(formData?.heightCm),
      "Weight": String(formData?.weight),
    };

    const missingFields = Object.entries(mandatoryFields)
      .filter(([, value]) => !value || value === "" || value === "undefined" || value === "null" || value === "NaN")
      .map(([key]) => key);

    if (missingFields.length > 0) {
      notification.error({
        message: "Missing Required Information",
        description: `Please ensure the following details are complete: ${missingFields.join(", ")}.`,
        duration: 6,
        placement: "top",
      });
      setIsPreparingDataLoading(false);
      return;
    }

    const payload = {
        requestId: requestId,
        patientid: patientCode,
        name: name,
        age: age,
        gender: gender,
        mobile: patientMobile,
        height: String(formData?.heightCm),
        height_units: "cm",
        weight: String(formData?.weight),
        weight_units: "kg",
        smoke: formData?.smoke || false,
        diabetes: formData?.diabetes || false,
        q1_perceived_stress: String(q1),
        q2_fatigue_level: String(q2),
        q3_sleep_quality_index: String(q3),
        q4_cognitive_impairment_index: String(q4),
        q5_overwork_index: String(q5),
        q6_mental_effort_index: String(q6),
        q7_nervousness: String(q7),
        q8_stress_level: String(q8),
       sourceChannel: patientData?.sourcechannel || loginObj?.sourcechannel || null,
    createdBy: patientData?.createdBy || loginObj?.createdBy || null,
    };
    console.log("✅ smartvital payload:", payload);
    try {
        const response = await axios.post(
            `${process.env.REACT_APP_QUANTUMURL}smartvital`,
            payload,
            {
                headers: {
                    "Content-Type": "application/json",
                    "accept": "application/json"
                }
            }
        );

        if (response.status === 200 && response.data?.status === "success") {
            setIsPreparingDataLoading(false);
            sessionStorage.removeItem("calculatedAnswers");
            stopCamera();
            return response.data;
        } else {
           throw new Error("Failed to submit video data.");
        }
    } catch (error) {
    console.error("❌ /smartvitals API failed:", error?.response?.data || error?.message || error);
    throw error;
}
  };

  const uploadVideo = async (blob) => {
    if (!blob || isSubmitted || isPreparingDataLoading) {
      return;
    }
    // Convert blob to File to ensure it has name and lastModified
    const file = new File([blob], "smart_vital_video.mp4", { type: "video/mp4" });

    // Trigger the Quantum upload modal
    setRecordedBlob(file);
    setShowUploadModal(true);
  };

  const phase = showUploadModal ? "complete" : isRecording ? "recording" : isCountdownActive ? "countdown" : "ready";
  const elapsed = RECORD_SECONDS - recordingTimeLeft;
  const perVital = RECORD_SECONDS / VITALS.length;
  // Progress indicator paced to the 30-second recording; values come from the report.
  const vitalStatus = (i) => {
    if (phase === "complete") return "captured";
    if (phase !== "recording") return "queued";
    if (elapsed >= (i + 1) * perVital) return "captured";
    if (elapsed >= i * perVital) return "reading";
    return "queued";
  };
  const capturedCount = VITALS.filter((_, i) => vitalStatus(i) === "captured").length;

  const liveChecks = [
    { key: "lighting", label: "Lighting" },
    { key: "centred", label: "Face centred" },
    { key: "single", label: "Single face" },
    phase === "ready" || phase === "countdown"
      ? { key: "distance", label: "Distance" }
      : { key: "movement", label: "No movement" },
  ];
  const goodCount = liveChecks.filter((c) => checks[c.key].ok).length;
  const allGood = goodCount === liveChecks.length;
  const quality = [
    { label: "Weak", bars: 1 },
    { label: "Weak", bars: 2 },
    { label: "Fair", bars: 3 },
    { label: "Good", bars: 4 },
    { label: "Strong", bars: 5 },
  ][goodCount];

  const ringOffset =
    phase === "recording" ? RING_C * (recordingTimeLeft / RECORD_SECONDS) : phase === "complete" ? 0 : RING_C;

  const headline = {
    ready: "Once you are ready, start recording.",
    countdown: "Get ready…",
    recording: "Recording in Progress... Please Stay Still",
    complete: "Your Face Scan is Complete!",
  }[phase];

  return (
    <>
      <Modal
        open={showCameraModal}
        title="Enable Camera Access"
        centered
        onCancel={() => setShowCameraModal(false)}
        footer={[
          <button
            key="retry"
            className="sv-btn sv-btn-primary"
            onClick={async () => {
              try {
                await initializeCamera();
                setShowCameraModal(false);
              } catch {}
            }}
          >
            Try Again
          </button>,
        ]}
      >
        <div style={{ color: "#5E626D" }}>
          <p>Camera access is blocked. Allow camera permission in your browser settings and retry.</p>
          <ul style={{ listStyle: "disc", marginLeft: 20, marginTop: 8 }}>
            <li>Click the lock icon near the address bar</li>
            <li>Set Camera permission to Allow</li>
            <li>Reload the page if prompted</li>
          </ul>
        </div>
      </Modal>

      <div className={`sv-scan is-${phase}`}>
        {/* Hidden (not unmounted) once "Wait Here" opens the upload page — same flow as before. */}
        <div className="sv-scan-body" style={uploadStage === "progress" ? { display: "none" } : undefined}>
        <h2 className="sv-scan-headline">{headline}</h2>

        <div className="sv-scan-grid">
          {/* ── Left column ── */}
          <div className="sv-scan-side">
            <div className="sv-card sv-panel">
              <div className="sv-panel-head">
                <span>Live checks</span>
                {phase === "ready" && (
                  <span className={`sv-pill${allGood ? "" : " is-warn"}`}>{allGood ? "You're ready" : "Adjusting"}</span>
                )}
              </div>
              <ul className="sv-checks">
                {liveChecks.map((c) => <CheckRow key={c.key} label={c.label} check={checks[c.key]} />)}
              </ul>
            </div>

            {phase === "ready" || phase === "countdown" ? (
              <div className="sv-card sv-panel">
                <div className="sv-panel-head"><span>During the scan</span></div>
                <ul className="sv-during">
                  <li><img src={scanIcons.time} alt="" /> It runs for 30 seconds and stops by itself.</li>
                  <li><img src={scanIcons.keepStill} alt="" /> Keep still and don't talk.</li>
                  <li><img src={scanIcons.breathe} alt="" /> Breathe normally.</li>
                </ul>
              </div>
            ) : (
              <div className="sv-card sv-panel">
                <div className="sv-panel-head">
                  <span>Pulse signal</span>
                  <span className={`sv-pill${goodCount >= 3 ? "" : " is-warn"}`}>{quality.label}</span>
                </div>
                <div className="sv-pulse"><PulseTrace animate={phase === "recording"} /></div>
                <p className="sv-pulse-label">Signal quality</p>
                <div className="sv-quality">
                  {Array.from({ length: 5 }).map((_, i) => <i key={i} className={i < quality.bars ? "is-on" : ""} />)}
                </div>
              </div>
            )}
          </div>

          {/* ── Camera ── */}
          <div className="sv-scan-stage">
            <svg className="sv-ring" viewBox="0 0 420 420" aria-hidden="true">
              <circle cx="210" cy="210" r={RING_R} className="sv-ring-track" />
              <circle
                cx="210"
                cy="210"
                r={RING_R}
                className={`sv-ring-progress${phase === "complete" ? " is-done" : ""}`}
                strokeDasharray={RING_C}
                strokeDashoffset={ringOffset}
              />
            </svg>

            <div className="sv-scan-circle">
              <video ref={videoRef} autoPlay muted playsInline className="sv-scan-video" />
              <canvas ref={canvasRef} className="sv-scan-canvas" />

              {(phase === "ready" || phase === "countdown") && (
                <>
                  <span className="sv-corner tl" /><span className="sv-corner tr" />
                  <span className="sv-corner bl" /><span className="sv-corner br" />
                  <span className="sv-oval" />
                  <span className="sv-scan-hint">Fit your face inside the dotted oval</span>
                </>
              )}
              {phase === "recording" && (
                <>
                  <span className="sv-oval is-faint" />
                  <span className="sv-scanline" />
                </>
              )}
              {phase === "countdown" && <span key={countdown} className="sv-countdown">{countdown}</span>}
              {phase === "complete" && (
                <div className="sv-scan-done">
                  <span className="sv-scan-done-icon"><Check size={30} strokeWidth={3} /></span>
                  <span>Recording saved</span>
                </div>
              )}
              {!isCameraOn && phase === "ready" && <span className="sv-scan-wait">Starting camera…</span>}
            </div>

            <span className={`sv-cam-chip is-${phase}`}>
              {phase === "complete" ? <Check size={11} strokeWidth={3} /> : <i />}
              {phase === "complete" ? "Done" : phase === "recording" ? "Recording" : isCameraOn ? "Camera on" : "Camera off"}
            </span>
          </div>

          {/* ── Right column ── */}
          <div className="sv-scan-side">
            <div className="sv-card sv-panel">
              {phase === "ready" || phase === "countdown" ? (
                <>
                  <div className="sv-panel-head">
                    <span>What we'll measure</span>
                    <span className="sv-panel-count">{VITALS.length} vitals</span>
                  </div>
                  <ul className="sv-vitals">
                    {VITALS.map(({ label, icon, bare }) => (
                      <li key={label}>
                        {bare ? (
                          <span className="sv-vital-icon"><img src={icon} alt="" width={15} height={15} /></span>
                        ) : (
                          <img className="sv-vital-icon" src={icon} alt="" />
                        )}
                        {label}
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <>
                  <div className="sv-panel-head">
                    <span>Reading your vitals</span>
                    <span className="sv-panel-count is-green">{capturedCount}/{VITALS.length}</span>
                  </div>
                  <ul className="sv-vitals is-status">
                    {VITALS.map(({ label }, i) => {
                      const s = vitalStatus(i);
                      return (
                        <li key={label} className={`is-${s}`}>
                          <span className="sv-vital-dot" />
                          <span className="sv-vital-name">{label}</span>
                          <span className="sv-vital-state">{s === "captured" ? "Captured" : s === "reading" ? "Reading" : "Queued"}</span>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>

        {phase === "ready" && (
          <div className="sv-scan-actions">
            <button type="button" className="sv-btn sv-btn-primary sv-btn-wide" onClick={startRecording} disabled={!faceDetector}>
              <span className="sv-rec-dot" /> {faceDetector ? "Start recording" : "Loading face detection…"}
            </button>
            {onCancel && (
              <button type="button" className="sv-btn sv-btn-ghost" onClick={onCancel}>Cancel</button>
            )}
          </div>
        )}
        {phase === "recording" && (
          <div className="sv-scan-actions">
            <span className="sv-timer"><b>{recordingTimeLeft}</b> sec left</span>
            <button type="button" className="sv-btn-outline sv-btn-cancel" onClick={stopRecording}>Cancel scan</button>
          </div>
        )}

        {phase !== "complete" && (
          <>
            <div className="sv-scan-tip">
              <img src={scanIcons.tipCard} alt="" />
              Breathe normally. Holding your breath can change your heart-rate reading.
            </div>
            <div className="sv-scan-tips">
              {TIPS.map(({ label, icon }) => (
                <span key={label}><img src={icon} alt="" /> {label}</span>
              ))}
            </div>
          </>
        )}
        </div>

        <SmartVitalUpload
          patientId={patientCode}
          serviceName="Smart Vitals"
          autoUploadFile={recordedBlob}
          initialVisible={showUploadModal}
          onCloseInitial={() => {
             setShowUploadModal(false);
             setRecordedBlob(null);
             resetRecordingState();
             // Back to the ready state for another attempt.
             initializeCamera();
          }}
          onUploadComplete={handleUploadComplete}
          onStageChange={(s) => {
            setUploadStage(s);
            onUploadPageChange?.(s === "progress");
          }}
        />
      </div>
    </>
  );
};

export default VideoCaptureComponent;
